import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { subDays } from 'date-fns';
import { analyticsService } from '../../../services/analyticsService';
import { cardService } from '../../../services/cardService';
import { useAuth } from '../../../contexts/AuthContext';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const CardAnalytics = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('30'); // 7, 30, 90, all

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cardData = await cardService.getCard(id, user.id);
        setCard(cardData);

        const end = new Date();
        const start = period === 'all' ? new Date(0) : subDays(end, parseInt(period, 10));

        const dashboardData = await analyticsService.getDashboardStats(start, end, id);
        setStats(dashboardData);
      } catch (err) {
        setError('Failed to load card analytics.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && id) fetchData();
  }, [user, id, period]);

  const chartOptions = useMemo(() => {
    if (!stats || !stats.trends || stats.trends.length === 0) return null;
    
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Opens', 'Connections'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
      xAxis: { 
        type: 'category', 
        boundaryGap: false, 
        data: stats.trends.map(t => new Date(t.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })) 
      },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        { name: 'Opens', type: 'line', data: stats.trends.map(t => t.opens), smooth: true, itemStyle: { color: '#3b82f6' } },
        { name: 'Connections', type: 'line', data: stats.trends.map(t => t.connections), smooth: true, itemStyle: { color: '#f59e0b' } },
      ]
    };
  }, [stats]);

  if (loading) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  if (error || !card) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">{error}</h2>
        <Button onClick={() => navigate('/app/cards')}>Back to Cards</Button>
      </div>
    );
  }

  const hasData = stats && stats.summary && stats.summary.card_opens > 0;
  const summary = stats?.summary || {};

  return (
    <div className="max-w-6xl pb-12">
      <div className="mb-6 flex items-center text-sm font-medium text-slate-500">
        <button onClick={() => navigate('/app/cards')} className="hover:text-slate-900 flex items-center">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Cards
        </button>
        <span className="mx-2">/</span>
        <button onClick={() => navigate(`/app/cards/${card.id}`)} className="hover:text-slate-900">
          {card.internal_name}
        </button>
        <span className="mx-2">/</span>
        <span className="text-slate-900">Analytics</span>
      </div>

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Card Analytics</h1>
          <p className="mt-2 text-slate-600">Performance insights for "{card.internal_name}"</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none bg-white"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
          <Button variant="secondary" onClick={() => navigate(`/app/cards/${card.id}`)}>
            View Card Info
          </Button>
        </div>
      </header>

      {!hasData ? (
        <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiIcons.FiBarChart2} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">No activity in this period</h3>
          <p className="text-slate-500 max-w-sm mx-auto mb-6">
            This card hasn't received any views or interactions during the selected date range.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <MetricCard label="Card Opens" value={summary.card_opens} />
            <MetricCard label="QR Scans" value={summary.qr_scans} />
            <MetricCard label="Contact Saves" value={summary.contact_saves} />
            <MetricCard label="Connections" value={summary.connections} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-base font-bold text-slate-900 mb-6">Engagement Trends</h3>
              <div className="h-80 w-full">
                {chartOptions ? (
                  <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">Not enough data to graph</div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-3">Source Attribution</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-slate-600">
                      <SafeIcon icon={FiIcons.FiMaximize} className="mr-2" /> QR Code
                    </div>
                    <span className="font-bold text-slate-900">{summary.qr_opens || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-slate-600">
                      <SafeIcon icon={FiIcons.FiLink} className="mr-2" /> Direct Link
                    </div>
                    <span className="font-bold text-slate-900">{summary.direct_opens || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-3">Action Clicks</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-slate-600">
                      <SafeIcon icon={FiIcons.FiGlobe} className="mr-2" /> Website
                    </div>
                    <span className="font-bold text-slate-900">{summary.website_clicks || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-slate-600">
                      <SafeIcon icon={FiIcons.FiMail} className="mr-2" /> Email
                    </div>
                    <span className="font-bold text-slate-900">{summary.email_clicks || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-slate-600">
                      <SafeIcon icon={FiIcons.FiPhone} className="mr-2" /> Phone
                    </div>
                    <span className="font-bold text-slate-900">{summary.phone_clicks || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-slate-600">
                      <SafeIcon icon={FiIcons.FiExternalLink} className="mr-2" /> Social Links
                    </div>
                    <span className="font-bold text-slate-900">{summary.link_clicks || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value }) => {
  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col">
      <p className="text-2xl font-bold text-slate-900 mb-1">{value || 0}</p>
      <p className="text-sm font-medium text-slate-500">{label}</p>
    </div>
  );
};